import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, LockIcon, PaperclipIcon, AlertCircleIcon } from 'lucide-react';
import { HELP_OPTIONS } from '../lib/constants';
import { Button } from './ui/Button';
import { cn } from '../lib/utils';
type FormState = {
  name: string;
  email: string;
  phone: string;
  topic: string;
  urgent: 'yes' | 'no' | '';
  message: string;
  contactMethod: string;
  fileName: string;
};
const empty: FormState = {
  name: '',
  email: '',
  phone: '',
  topic: '',
  urgent: '',
  message: '',
  contactMethod: 'Phone call',
  fileName: ''
};
const labelCls = 'mb-1.5 block text-sm font-medium text-navy';
const fieldCls = 'w-full rounded-md border border-navy/15 bg-white px-3.5 py-2.5 text-[0.95rem] text-ink placeholder:text-ink/35 transition-colors focus:border-brass focus:outline-none focus:ring-2 focus:ring-brass/20';
export function EnquiryForm({
  defaultTopic
}: {
  defaultTopic?: string;
}) {
  const [form, setForm] = useState<FormState>({
    ...empty,
    topic: defaultTopic ?? ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const update = (key: keyof FormState, value: string) => {
    setForm(f => ({
      ...f,
      [key]: value
    }));
    setErrors(e => ({
      ...e,
      [key]: ''
    }));
  };
  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Please enter your name.';
    if (!form.email.trim()) next.email = 'Please enter your email.';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Enter a valid email address.';
    if (!form.phone.trim()) next.phone = 'Please enter a phone number.';
    if (!form.topic) next.topic = 'Please choose an area.';
    if (!form.message.trim()) next.message = 'A sentence or two is enough to get started.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 900);
  };
  if (submitted) {
    return <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="rounded-xl border border-sage/40 bg-sage/10 p-8 text-center" data-id="element-13">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sage/20" data-id="element-14">
          <CheckCircle2Icon className="h-6 w-6 text-sage" data-id="element-15" />
        </div>
        <h3 className="mt-4 font-serif text-2xl text-navy" data-id="element-16">
          Thank you — we've got it.
        </h3>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink/70" data-id="element-17">
          Your enquiry has been received and will be directed to the most
          appropriate team. We typically respond within one business day. If
          your matter is urgent, please call{' '}
          <a href="tel:+61893893999" className="font-semibold text-brass-dark" data-id="element-18">
            08 9389 3999
          </a>
          .
        </p>
      </motion.div>;
  }
  return <form onSubmit={onSubmit} noValidate className="space-y-4" data-id="element-19">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-20">
        <Field label="Name" error={errors.name} data-id="element-21">
          <input className={cn(fieldCls, errors.name && 'border-red-400')} value={form.name} onChange={e => update('name', e.target.value)} placeholder="Your full name" autoComplete="name" data-id="element-22" />
        </Field>
        <Field label="Phone" error={errors.phone} data-id="element-23">
          <input className={cn(fieldCls, errors.phone && 'border-red-400')} value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="Best contact number" inputMode="tel" autoComplete="tel" data-id="element-24" />
        </Field>
      </div>

      <Field label="Email" error={errors.email} data-id="element-25">
        <input className={cn(fieldCls, errors.email && 'border-red-400')} value={form.email} onChange={e => update('email', e.target.value)} placeholder="you@example.com" type="email" autoComplete="email" data-id="element-26" />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-27">
        <Field label="What do you need help with?" error={errors.topic} data-id="element-28">
          <select className={cn(fieldCls, 'appearance-none', errors.topic && 'border-red-400')} value={form.topic} onChange={e => update('topic', e.target.value)} data-id="element-29">
            <option value="" data-id="element-30">Choose an area…</option>
            {HELP_OPTIONS.map(o => <option key={o} value={o} data-id="element-31">
                {o}
              </option>)}
          </select>
        </Field>
        <Field label="Is it urgent?" data-id="element-32">
          <div className="flex gap-2" data-id="element-33">
            {(['yes', 'no'] as const).map(v => <button key={v} type="button" onClick={() => update('urgent', v)} className={cn('flex-1 rounded-md border px-3 py-2.5 text-sm font-medium capitalize transition-colors', form.urgent === v ? 'border-brass bg-brass/10 text-brass-dark' : 'border-navy/15 text-ink/70 hover:border-navy/30')} data-id="element-34">
                {v}
              </button>)}
          </div>
        </Field>
      </div>

      <Field label="Briefly tell us what has happened" error={errors.message} data-id="element-35">
        <textarea className={cn(fieldCls, 'min-h-[110px] resize-y', errors.message && 'border-red-400')} value={form.message} onChange={e => update('message', e.target.value)} placeholder="A short summary is fine — you don't need to explain everything perfectly." data-id="element-36" />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-37">
        <Field label="Preferred contact method" data-id="element-38">
          <select className={cn(fieldCls, 'appearance-none')} value={form.contactMethod} onChange={e => update('contactMethod', e.target.value)} data-id="element-39">
            {['Phone call', 'Email', 'Either is fine'].map(o => <option key={o} value={o} data-id="element-40">
                {o}
              </option>)}
          </select>
        </Field>
        <Field label="Upload a document (optional)" data-id="element-41">
          <label className={cn(fieldCls, 'flex cursor-pointer items-center gap-2 text-ink/60')} data-id="element-42">
            <PaperclipIcon className="h-4 w-4 shrink-0" data-id="element-43" />
            <span className="truncate" data-id="element-44">
              {form.fileName || 'Attach a contract, notice or letter'}
            </span>
            <input type="file" className="hidden" onChange={e => update('fileName', e.target.files?.[0]?.name ?? '')} data-id="element-45" />
          </label>
        </Field>
      </div>

      <Button type="submit" variant="primary" size="lg" className="w-full" disabled={submitting} data-id="element-46">
        {submitting ? 'Sending…' : 'Request a call back'}
      </Button>

      <p className="flex items-center justify-center gap-2 text-center text-xs text-ink/55" data-id="element-47">
        <LockIcon className="h-3.5 w-3.5 text-sage" data-id="element-48" />
        Your enquiry is confidential. We will direct it to the most appropriate
        team.
      </p>
    </form>;
}
function Field({
  label,
  error,
  children
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-49">
      <label className={labelCls} data-id="element-50">{label}</label>
      {children}
      {error && <p className="mt-1 flex items-center gap-1 text-xs text-red-500" data-id="element-51">
          <AlertCircleIcon className="h-3.5 w-3.5" data-id="element-52" /> {error}
        </p>}
    </div>;
}